import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { redoIcon, undoIcon } from "assets/icons";

import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import Button from "../../components/Buttons/Button";
import IconButton from "../../components/Buttons/IconButton";
import DragAndDropField from "../../components/DragAndDropField/DragAndDropField";
import EmptyState from "../../components/EmptyState";
import { ConfirmModal } from "../../components/Modals";
import Sidebar from "../../components/Sidebar";
import { Spinner } from "../../components/Spinner";
import StatusBadge from "../../components/StatusBadge";
import { useNotify } from "../../context/NotificationContext";
import {
  deleteNewsArticle,
  fetchManagedArticle,
  hydrateArticleContent,
  publishNewsArticle,
  stripTransientAssetUrls,
  updateNewsArticle,
  uploadNewsMedia,
  uploadNewsVideo,
} from "../../services/newsService/newsService";
import type {
  EditorContent,
  NewsArticleDetail,
  NewsMediaAsset,
  NewsVideoAsset,
} from "../../services/newsService/newsService.types";
import { logger } from "../../utils/logger";
import { CoursesPageContainer } from "../CoursesPage/CoursesPage.styles";
import ArticleRenderer from "./ArticleRenderer";
import NewsBlockEditor, { NewsBlockEditorHandle } from "./NewsBlockEditor";
import {
  ArticleCover,
  ArticleDate,
  ArticleExcerpt,
  ArticleHeader,
  ArticleNumber,
  ArticleTitle,
  CoverControls,
  CoverPreview,
  CoverPreviewFrame,
  CoverRemoveButton,
  EditorActions,
  EditorBreadcrumbBar,
  EditorFooter,
  EditorFooterMeta,
  EditorForm,
  EditorFrame,
  EditorMain,
  EditorScrollArea,
  FieldInput,
  FieldLabel,
  FieldTextarea,
  LoadingSlot,
  NewsMain,
  NewsShell,
  PreviewFrame,
  SaveState,
} from "./NewsPage.styles";

const statusLabels = {
  draft: "Черновик",
  published: "Опубликовано",
};

const statusVariants = {
  draft: "red",
  published: "brand",
} as const;

const NewsEditorPage: React.FC = () => {
  const { articleId = "" } = useParams();
  const id = Number(articleId);
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useNotify();
  const editorRef = useRef<NewsBlockEditorHandle | null>(null);
  const coverUndoRef = useRef<Array<NewsMediaAsset | null>>([]);
  const coverRedoRef = useRef<Array<NewsMediaAsset | null>>([]);
  const coverWasLastChangeRef = useRef(false);
  const lastUndoWasCoverRef = useRef(false);

  const [article, setArticle] = useState<NewsArticleDetail | null>(null);
  const [editorData, setEditorData] = useState<EditorContent | null>(null);
  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [cover, setCover] = useState<NewsMediaAsset | null>(null);
  const [previewContent, setPreviewContent] = useState<EditorContent | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"publish" | "savePublished" | null>(null);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [isUnsavedNewDraft, setIsUnsavedNewDraft] = useState(
    Boolean((location.state as { newDraft?: boolean } | null)?.newDraft),
  );
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    if (!id) {
      setError("Некорректная статья.");
      setLoading(false);
      return;
    }
    fetchManagedArticle(id)
      .then((data) => {
        if (!active) return;
        setArticle(data);
        setTitle(data.title);
        setExcerpt(data.excerpt || "");
        setCover(data.cover);
        coverUndoRef.current = [];
        coverRedoRef.current = [];
        coverWasLastChangeRef.current = false;
        lastUndoWasCoverRef.current = false;
        setEditorData(hydrateArticleContent(data));
      })
      .catch((requestError) => {
        logger.error("Не удалось загрузить редактор новости:", requestError);
        if (active) setError("Нет доступа к этой статье или она не найдена.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  const articleIdForPolling = article?.id;
  const hasProcessingVideo = Boolean(
    article?.video_assets.some((video) => video.status === "processing"),
  );

  useEffect(() => {
    if (!articleIdForPolling || !hasProcessingVideo) return;
    const timer = window.setInterval(() => {
      fetchManagedArticle(articleIdForPolling)
        .then((fresh) => {
          setArticle((current) =>
            current
              ? {
                  ...current,
                  video_assets: fresh.video_assets,
                  media_assets: fresh.media_assets,
                }
              : current,
          );
        })
        .catch(() => undefined);
    }, 4000);
    return () => window.clearInterval(timer);
  }, [articleIdForPolling, hasProcessingVideo]);

  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);

  const saveArticle = async (showSuccessToast = true): Promise<NewsArticleDetail | null> => {
    if (!article || !title.trim()) {
      toast.show({
        severity: "warn",
        summary: "Заполните заголовок",
        detail: "Заголовок статьи обязателен.",
        life: 3000,
      });
      return null;
    }
    setSaving(true);
    try {
      const content = editorRef.current
        ? await editorRef.current.save()
        : editorData || article.content_json;
      const saved = await updateNewsArticle(article.id, {
        title: title.trim(),
        excerpt: excerpt.trim(),
        cover_media_id: cover?.id ?? null,
        content_json: stripTransientAssetUrls(content),
      });
      const hydrated = hydrateArticleContent(saved);
      setArticle(saved);
      setEditorData(hydrated);
      setCover(saved.cover);
      setDirty(false);
      if (isUnsavedNewDraft) {
        setIsUnsavedNewDraft(false);
        navigate(location.pathname, { replace: true, state: null });
      }
      if (showSuccessToast) {
        toast.show({
          severity: "success",
          summary: "Сохранено",
          detail: "Изменения статьи сохранены.",
          life: 1800,
        });
      }
      return saved;
    } catch (saveError) {
      logger.error("Не удалось сохранить статью:", saveError);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось сохранить статью.",
        life: 3200,
      });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    if (!article) return;
    try {
      const content = editorRef.current
        ? await editorRef.current.save()
        : editorData || article.content_json;
      setPreviewContent(content);
      setPreviewOpen(true);
    } catch (previewError) {
      logger.error("Не удалось собрать предпросмотр:", previewError);
    }
  };

  const publish = async () => {
    if (!article) return;
    setPublishing(true);
    try {
      const saved = await saveArticle(false);
      if (!saved) return;
      const published = await publishNewsArticle(saved.id);
      setArticle(published);
      setEditorData(hydrateArticleContent(published));
      setCover(published.cover);
      setPreviewOpen(false);
      setDirty(false);
      window.dispatchEvent(new Event("news-status-changed"));
      toast.show({
        severity: "success",
        summary: "Опубликовано",
        detail: "Статья доступна в ленте новостей.",
        life: 2600,
      });
    } catch (publishError) {
      logger.error("Не удалось опубликовать статью:", publishError);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось опубликовать статью.",
        life: 3200,
      });
    } finally {
      setPublishing(false);
    }
  };

  const leaveEditor = async () => {
    if (!article || leaving) return;

    setLeaving(true);
    try {
      if (isUnsavedNewDraft) {
        await deleteNewsArticle(article.id);
      }
      navigate("/news/manage");
    } catch (leaveError) {
      logger.error("Не удалось отменить редактирование статьи:", leaveError);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: isUnsavedNewDraft
          ? "Не удалось удалить несохранённый черновик."
          : "Не удалось выйти из редактора.",
        life: 3000,
      });
    } finally {
      setLeaving(false);
    }
  };

  const requestLeaveEditor = () => {
    if (dirty) {
      setLeaveConfirmOpen(true);
      return;
    }
    void leaveEditor();
  };

  const confirmLeaveEditor = async () => {
    setLeaveConfirmOpen(false);
    await leaveEditor();
  };

  const requestSave = () => {
    if (!article) return;
    if (article.status === "published") {
      setConfirmAction("savePublished");
      return;
    }
    void saveArticle();
  };

  const confirmEditorAction = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (action === "savePublished") {
      await saveArticle();
      return;
    }
    if (action === "publish") {
      await publish();
    }
  };

  const uploadEditorMedia = async (file: File): Promise<NewsMediaAsset> => {
    if (!article) throw new Error("Статья не загружена");
    const asset = await uploadNewsMedia(article.id, file);
    setArticle((current) =>
      current
        ? { ...current, media_assets: [...current.media_assets, asset] }
        : current,
    );
    setDirty(true);
    return asset;
  };

  const uploadEditorVideo = async (file: File): Promise<NewsVideoAsset> => {
    if (!article) throw new Error("Статья не загружена");
    const asset = await uploadNewsVideo(article.id, file);
    setArticle((current) =>
      current
        ? { ...current, video_assets: [...current.video_assets, asset] }
        : current,
    );
    setDirty(true);
    return asset;
  };

  const applyCoverChange = (nextCover: NewsMediaAsset | null) => {
    if ((cover?.id ?? null) === (nextCover?.id ?? null)) return;
    coverUndoRef.current.push(cover);
    if (coverUndoRef.current.length > 20) coverUndoRef.current.shift();
    coverRedoRef.current = [];
    setCover(nextCover);
    setDirty(true);
    coverWasLastChangeRef.current = true;
    lastUndoWasCoverRef.current = false;
  };

  const undoEditor = useCallback(() => {
    if (coverWasLastChangeRef.current && coverUndoRef.current.length) {
      const previousCover = coverUndoRef.current.pop() ?? null;
      coverRedoRef.current.push(cover);
      setCover(previousCover);
      setDirty(true);
      coverWasLastChangeRef.current = false;
      lastUndoWasCoverRef.current = true;
      return;
    }
    lastUndoWasCoverRef.current = false;
    editorRef.current?.undo();
  }, [cover]);

  const redoEditor = useCallback(() => {
    if (lastUndoWasCoverRef.current && coverRedoRef.current.length) {
      const nextCover = coverRedoRef.current.pop() ?? null;
      coverUndoRef.current.push(cover);
      setCover(nextCover);
      setDirty(true);
      coverWasLastChangeRef.current = true;
      lastUndoWasCoverRef.current = false;
      return;
    }
    editorRef.current?.redo();
  }, [cover]);

  useEffect(() => {
    const handleHistoryShortcut = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return;

      const key = event.key.toLowerCase();
      const isUndo = key === "z" && !event.shiftKey;
      const isRedo = key === "y" || (key === "z" && event.shiftKey);
      if (!isUndo && !isRedo) return;

      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest("input, textarea, select")) {
        return;
      }

      event.preventDefault();
      if (isUndo) {
        undoEditor();
      } else {
        redoEditor();
      }
    };

    window.addEventListener("keydown", handleHistoryShortcut);
    return () => window.removeEventListener("keydown", handleHistoryShortcut);
  }, [redoEditor, undoEditor]);

  const uploadCover = async (file: File) => {    if (!article) return;
    if (!file.type.startsWith("image/")) {
      toast.show({
        severity: "warn",
        summary: "Нужна картинка",
        detail: "Для обложки выберите изображение.",
        life: 2600,
      });
      return;
    }
    setCoverUploading(true);
    try {
      const asset = await uploadNewsMedia(article.id, file);
      setArticle((current) =>
        current
          ? { ...current, media_assets: [...current.media_assets, asset] }
          : current,
      );
      applyCoverChange(asset);
    } finally {
      setCoverUploading(false);
    }
  };

  const previewArticle =
    article && previewContent
      ? {
          content: previewContent,
          media: article.media_assets,
          videos: article.video_assets,
        }
      : null;

  if (loading) {
    return (
      <CoursesPageContainer>
        <Sidebar />
        <NewsMain>
          <NewsShell>
            <Breadcrumbs
              items={[
                { label: "Новости", to: "/news" },
                { label: "Управление новостями", to: "/news/manage" },
                { label: "Редактор статьи" },
              ]}
            />
            <LoadingSlot aria-label="Загрузка редактора">
              <Spinner $size="2rem" />
            </LoadingSlot>
          </NewsShell>
        </NewsMain>
      </CoursesPageContainer>
    );
  }

  if (error || !article || !editorData) {
    return (
      <CoursesPageContainer>
        <Sidebar />
        <NewsMain>
          <NewsShell>
            <Breadcrumbs
              items={[
                { label: "Новости", to: "/news" },
                { label: "Управление новостями", to: "/news/manage" },
                { label: "Редактор статьи" },
              ]}
            />
            <EmptyState
              title="Статья недоступна"
              description={error || "Статья не найдена."}
            />
          </NewsShell>
        </NewsMain>
      </CoursesPageContainer>
    );
  }

  return (
    <CoursesPageContainer>
      <Sidebar />
      <EditorMain>
        <EditorScrollArea>
          <NewsShell>
            <EditorBreadcrumbBar>
              <Breadcrumbs
                items={[
                  { label: "Новости", to: "/news" },
                  { label: "Управление новостями", to: "/news/manage" },
                  { label: article.title || "Без названия" },
                ]}
              />
            </EditorBreadcrumbBar>

            <EditorForm>
              <FieldLabel>
                Заголовок
                <FieldInput
                  value={title}
                  maxLength={500}
                  onChange={(event) => {
                    setTitle(event.target.value);
                    setDirty(true);
                  }}
                  placeholder="Заголовок статьи"
                />
              </FieldLabel>
              <FieldLabel>
                Краткий анонс
                <FieldTextarea
                  value={excerpt}
                  onChange={(event) => {
                    setExcerpt(event.target.value);
                    setDirty(true);
                  }}
                  placeholder="Коротко о содержании статьи"
                />
              </FieldLabel>
              <FieldLabel>
                Обложка
                <CoverControls>
                  {cover ? (
                    <CoverPreviewFrame>
                      <CoverPreview src={cover.url} alt="" />
                      <CoverRemoveButton
                        type="button"
                        aria-label="Убрать обложку"
                        onClick={() => applyCoverChange(null)}
                      >
                        ×
                      </CoverRemoveButton>
                    </CoverPreviewFrame>
                  ) : null}
                  <DragAndDropField
                    accept="image/*"
                    files={[]}
                    maxFiles={1}
                    showFileList={false}
                    variant="compact"
                    disabled={coverUploading}
                    placeholder={
                      coverUploading
                        ? "Загружаем обложку…"
                        : cover
                        ? "Заменить обложку"
                        : "Добавить обложку"
                    }
                    infoText="Выберите изображение или перетащите его сюда"
                    onFileSelected={(files) => {
                      const file = files?.[0];
                      if (file) void uploadCover(file);
                    }}
                  />
                </CoverControls>
              </FieldLabel>
            </EditorForm>

            {previewOpen && previewArticle ? (
              <PreviewFrame>
                <ArticleHeader>
                  <ArticleNumber>
                    {article.number ? "№ " + article.number + " · " : ""}
                    Предпросмотр
                  </ArticleNumber>
                  <ArticleTitle>{title || "Без заголовка"}</ArticleTitle>
                  {excerpt ? <ArticleExcerpt>{excerpt}</ArticleExcerpt> : null}
                  <ArticleDate>
                    Предпросмотр не меняет статус статьи и не отправляет уведомления.
                  </ArticleDate>
                </ArticleHeader>
                {cover ? <ArticleCover src={cover.url} alt="" /> : null}
                <ArticleRenderer
                  content={previewArticle.content}
                  mediaAssets={previewArticle.media}
                  videoAssets={previewArticle.videos}
                />
              </PreviewFrame>
            ) : (
              <EditorFrame>
                <NewsBlockEditor
                  key={article.id}
                  ref={editorRef}
                  articleId={article.id}
                  data={editorData}
                  uploadMedia={uploadEditorMedia}
                  uploadVideo={uploadEditorVideo}
                  onChange={() => {
                    coverWasLastChangeRef.current = false;
                    lastUndoWasCoverRef.current = false;
                    setDirty(true);
                  }}
                />
              </EditorFrame>
            )}

            <ConfirmModal
              isOpen={leaveConfirmOpen}
              title={
                isUnsavedNewDraft
                  ? "Отменить создание статьи?"
                  : "Выйти без сохранения?"
              }
              message={
                isUnsavedNewDraft
                  ? "Черновик и все несохранённые изменения будут удалены."
                  : "Несохранённые изменения будут потеряны."
              }
              confirmButtonText={
                isUnsavedNewDraft ? "Отменить создание" : "Выйти без сохранения"
              }
              confirmVariant="danger"
              onConfirm={() => void confirmLeaveEditor()}
              onCancel={() => setLeaveConfirmOpen(false)}
            />

            <ConfirmModal
              isOpen={confirmAction !== null}
              title={
                confirmAction === "savePublished"
                  ? "Сохранить изменения в опубликованной новости?"
                  : "Опубликовать статью?"
              }
              message={
                confirmAction === "savePublished"
                  ? "Изменения сразу отразятся в новости, которая уже опубликована и доступна пользователям."
                  : "После публикации статья станет доступна пользователям в разделе «Новости»."
              }
              confirmButtonText={
                confirmAction === "savePublished"
                  ? saving
                    ? "Сохраняем…"
                    : "Сохранить"
                  : publishing
                  ? "Публикуем…"
                  : "Опубликовать"
              }
              confirmVariant="primary"
              onConfirm={() => void confirmEditorAction()}
              onCancel={() => setConfirmAction(null)}
            />
          </NewsShell>
        </EditorScrollArea>

        <EditorFooter>
          <EditorFooterMeta>
            <StatusBadge
              text={previewOpen ? "Предпросмотр" : statusLabels[article.status]}
              variant={previewOpen ? "neutral" : statusVariants[article.status]}
            />
            <SaveState>
              {previewOpen
                ? "Без публикации"
                : dirty
                ? "Есть несохранённые изменения"
                : isUnsavedNewDraft
                ? "Новый черновик"
                : "Все изменения сохранены"}
            </SaveState>
          </EditorFooterMeta>

          <EditorActions>
            {previewOpen ? (
              <Button variant="secondary" size="sm" onClick={() => setPreviewOpen(false)}>
                Вернуться к редактору
              </Button>
            ) : (
              <>
                <IconButton
                  icon={undoIcon}
                  altText="Отменить (Ctrl+Z)"
                  className="news-history-action"
                  onClick={undoEditor}
                />
                <IconButton
                  icon={redoIcon}
                  altText="Повторить (Ctrl+Shift+Z)"
                  className="news-history-action"
                  onClick={redoEditor}
                />
                <Button variant="secondary" size="sm" onClick={() => void openPreview()}>
                  Предпросмотр
                </Button>
                <Button
                  variant="dangerSecondary"
                  size="sm"
                  disabled={leaving}
                  onClick={requestLeaveEditor}
                >
                  {leaving ? "Выходим..." : "Отмена"}
                </Button>
                <Button variant="secondary" size="sm" loading={saving} onClick={requestSave}>
                  Сохранить
                </Button>
                {article.status === "draft" ? (
                  <Button
                    variant="primary"
                    size="sm"
                    loading={publishing}
                    onClick={() => setConfirmAction("publish")}
                  >
                    Опубликовать
                  </Button>
                ) : null}
              </>
            )}
          </EditorActions>
        </EditorFooter>
      </EditorMain>
    </CoursesPageContainer>
  );
};

export default NewsEditorPage;